"use client";

import { useState, useMemo } from "react";
import { motion } from "motion/react";
import { cn } from "./utils";

// ============================================
// TYPES
// ============================================

export interface CapTableEntry {
  id: string;
  name: string;
  type: 'founder' | 'employee' | 'investor' | 'esop' | 'advisor' | 'other';
  ownershipPercentage: number;
}

export interface TermSheetData {
  id: string;
  fundName: string;
  investmentAmount: number;
  preMoneyValuation: number;
  status: 'received' | 'reviewing' | 'negotiating' | 'accepted' | 'declined' | 'expired';
}

export interface DilutionSimulatorProps {
  capTable: CapTableEntry[];
  termSheets: TermSheetData[];
  formatCurrency?: (value: number) => string;
  className?: string;
}

// ============================================
// CONSTANTS
// ============================================

const TYPE_COLORS: Record<string, { fill: string; bg: string; text: string }> = {
  founder: { fill: '#3b82f6', bg: 'bg-blue-500', text: 'text-blue-600' },
  employee: { fill: '#6366f1', bg: 'bg-indigo-500', text: 'text-indigo-600' },
  investor: { fill: '#8b5cf6', bg: 'bg-purple-500', text: 'text-purple-600' },
  esop: { fill: '#f59e0b', bg: 'bg-amber-500', text: 'text-amber-600' },
  advisor: { fill: '#14b8a6', bg: 'bg-teal-500', text: 'text-teal-600' },
  other: { fill: '#64748b', bg: 'bg-slate-500', text: 'text-slate-600' },
  committed: { fill: '#10b981', bg: 'bg-emerald-500', text: 'text-emerald-600' },
  pending: { fill: '#eab308', bg: 'bg-yellow-500', text: 'text-yellow-600' },
};

const TYPE_LABELS: Record<string, string> = {
  founder: 'Founders',
  employee: 'Employees',
  investor: 'Previous Investors',
  esop: 'ESOP Pool',
  advisor: 'Advisors',
  other: 'Other',
};

type ViewMode = 'current' | 'committed' | 'pending' | 'all' | 'custom';

// ============================================
// MAIN COMPONENT
// ============================================

export function DilutionChart({
  capTable,
  termSheets,
  formatCurrency = (v) => `$${(v / 1000000).toFixed(1)}M`,
  className,
}: DilutionSimulatorProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [selectedTermSheets, setSelectedTermSheets] = useState<Set<string>>(new Set());
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);

  // Categorize term sheets
  const committedTermSheets = termSheets.filter(ts => ts.status === 'accepted');
  const pendingTermSheets = termSheets.filter(ts => 
    ['received', 'reviewing', 'negotiating'].includes(ts.status)
  );

  // Determine which term sheets to include based on view mode
  const activeTermSheets = useMemo(() => {
    switch (viewMode) {
      case 'current':
        return [];
      case 'committed':
        return committedTermSheets;
      case 'pending':
        return pendingTermSheets;
      case 'all':
        return [...committedTermSheets, ...pendingTermSheets];
      case 'custom':
        return termSheets.filter(ts => selectedTermSheets.has(ts.id));
      default:
        return [];
    }
  }, [viewMode, selectedTermSheets, committedTermSheets, pendingTermSheets, termSheets]);

  // Toggle a term sheet in custom mode
  const toggleTermSheet = (id: string) => {
    const newSet = new Set(selectedTermSheets);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedTermSheets(newSet);
    setViewMode('custom');
  };

  // Calculate diluted ownership
  const calculations = useMemo(() => {
    // Total investment and dilution from selected term sheets
    const totalInvestment = activeTermSheets.reduce((sum, ts) => sum + ts.investmentAmount, 0);
    
    // For multiple term sheets, we need to calculate cumulative dilution
    // Using average valuation if different, or calculate sequentially
    let cumulativeDilution = 0;
    
    if (activeTermSheets.length > 0) {
      // Calculate each term sheet's dilution individually then combine
      activeTermSheets.forEach(ts => {
        const postMoney = ts.preMoneyValuation + ts.investmentAmount;
        const dilution = (ts.investmentAmount / postMoney) * 100;
        // Compound dilution: each new investor dilutes existing holders
        cumulativeDilution = cumulativeDilution + (1 - cumulativeDilution / 100) * dilution;
      });
    }

    // Current cap table total (should be ~100%)
    const currentTotal = capTable.reduce((sum, e) => sum + e.ownershipPercentage, 0);
    
    // Calculate diluted ownership for each cap table entry
    const dilutedCapTable = capTable.map(entry => ({
      ...entry,
      dilutedPercentage: entry.ownershipPercentage * (1 - cumulativeDilution / 100)
    }));

    // Calculate ownership from term sheets
    const termSheetOwnership = activeTermSheets.map(ts => {
      const postMoney = ts.preMoneyValuation + ts.investmentAmount;
      const ownership = (ts.investmentAmount / postMoney) * 100;
      // Adjust for other investors diluting this one
      // This is simplified - in reality each subsequent investor dilutes previous
      return {
        ...ts,
        ownershipPercentage: ownership * (1 - (cumulativeDilution - ownership) / 100 * 0.5) // Simplified
      };
    });

    // Aggregate by type for cap table
    const typeBreakdown: Record<string, number> = {};
    dilutedCapTable.forEach(entry => {
      typeBreakdown[entry.type] = (typeBreakdown[entry.type] || 0) + entry.dilutedPercentage;
    });

    return {
      totalInvestment,
      totalDilution: cumulativeDilution,
      currentTotal,
      dilutedCapTable,
      termSheetOwnership,
      typeBreakdown,
      foundersRemaining: typeBreakdown['founder'] || 0,
    };
  }, [capTable, activeTermSheets]);

  // Generate pie chart segments
  const pieSegments = useMemo(() => {
    const segments: { id: string; name: string; percentage: number; color: string; type: string }[] = [];
    
    // Add diluted cap table entries by type
    Object.entries(calculations.typeBreakdown).forEach(([type, percentage]) => {
      if (percentage > 0) {
        segments.push({
          id: `type-${type}`,
          name: TYPE_LABELS[type] || type,
          percentage,
          color: TYPE_COLORS[type]?.fill || '#94a3b8',
          type,
        });
      }
    });

    // Add term sheet ownership
    if (viewMode !== 'current') {
      const committedTotal = activeTermSheets
        .filter(ts => ts.status === 'accepted')
        .reduce((sum, ts) => {
          const postMoney = ts.preMoneyValuation + ts.investmentAmount;
          return sum + (ts.investmentAmount / postMoney) * 100;
        }, 0);

      const pendingTotal = activeTermSheets
        .filter(ts => ts.status !== 'accepted')
        .reduce((sum, ts) => {
          const postMoney = ts.preMoneyValuation + ts.investmentAmount;
          return sum + (ts.investmentAmount / postMoney) * 100;
        }, 0);

      if (committedTotal > 0) {
        segments.push({
          id: 'committed-investors',
          name: 'Committed Investors',
          percentage: committedTotal,
          color: TYPE_COLORS.committed.fill,
          type: 'committed',
        });
      }

      if (pendingTotal > 0) {
        segments.push({
          id: 'pending-investors',
          name: 'Pending Term Sheets',
          percentage: pendingTotal,
          color: TYPE_COLORS.pending.fill,
          type: 'pending',
        });
      }
    }

    return segments;
  }, [calculations, viewMode, activeTermSheets]);

  // Generate SVG paths for donut chart
  const donutPaths = useMemo(() => {
    const paths: { path: string; color: string; segment: typeof pieSegments[0] }[] = [];
    let currentAngle = -90;
    const radius = 80;
    const innerRadius = 50;
    const centerX = 100;
    const centerY = 100;

    pieSegments.forEach((segment) => {
      const angle = (segment.percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;

      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      const x1 = centerX + radius * Math.cos(startRad);
      const y1 = centerY + radius * Math.sin(startRad);
      const x2 = centerX + radius * Math.cos(endRad);
      const y2 = centerY + radius * Math.sin(endRad);
      const x3 = centerX + innerRadius * Math.cos(endRad);
      const y3 = centerY + innerRadius * Math.sin(endRad);
      const x4 = centerX + innerRadius * Math.cos(startRad);
      const y4 = centerY + innerRadius * Math.sin(startRad);

      const largeArcFlag = angle > 180 ? 1 : 0;

      const path = [
        `M ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        `L ${x3} ${y3}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
        'Z',
      ].join(' ');

      paths.push({ path, color: segment.color, segment });
      currentAngle = endAngle;
    });

    return paths;
  }, [pieSegments]);

  const hasCapTable = capTable.length > 0;
  const hasTermSheets = termSheets.length > 0;

  return (
    <div className={cn("space-y-6", className)}>
      {/* View Mode Toggles */}
      <div className="flex flex-wrap gap-2">
        {(['current', 'committed', 'pending', 'all'] as const).map((mode) => {
          const isActive = viewMode === mode;
          const count = mode === 'committed' ? committedTermSheets.length :
                        mode === 'pending' ? pendingTermSheets.length :
                        mode === 'all' ? termSheets.length : 0;
          return (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-lg border transition-all",
                isActive
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
              )}
            >
              {mode === 'current' && 'Current'}
              {mode === 'committed' && `+ Committed (${count})`}
              {mode === 'pending' && `+ Pending (${count})`}
              {mode === 'all' && `All (${count})`}
            </button>
          );
        })}
      </div>

      {/* Main Content */}
      {!hasCapTable ? (
        <div className="text-center py-8 px-4 bg-neutral-50 rounded-xl border border-dashed border-neutral-200">
          <div className="text-neutral-400 mb-2">No cap table configured</div>
          <p className="text-xs text-neutral-400">
            Add shareholders in Raise Settings → Cap Table
          </p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Donut Chart */}
          <div className="flex-shrink-0 flex items-center justify-center">
            <div className="relative">
              <svg width="200" height="200" viewBox="0 0 200 200">
                {donutPaths.length > 0 ? (
                  donutPaths.map(({ path, color, segment }) => (
                    <motion.path
                      key={segment.id}
                      d={path}
                      fill={color}
                      initial={{ opacity: 0.9 }}
                      animate={{
                        opacity: hoveredSegment === segment.id || hoveredSegment === null ? 1 : 0.4,
                        scale: hoveredSegment === segment.id ? 1.02 : 1,
                      }}
                      style={{ transformOrigin: '100px 100px' }}
                      onMouseEnter={() => setHoveredSegment(segment.id)}
                      onMouseLeave={() => setHoveredSegment(null)}
                      className="cursor-pointer transition-all duration-200"
                    />
                  ))
                ) : (
                  <circle cx="100" cy="100" r="65" fill="#f5f5f5" />
                )}
                {/* Center text */}
                <text x="100" y="92" textAnchor="middle" className="fill-neutral-900 text-xl font-semibold">
                  {calculations.foundersRemaining.toFixed(1)}%
                </text>
                <text x="100" y="112" textAnchor="middle" className="fill-neutral-500 text-[10px]">
                  Founders
                </text>
              </svg>
            </div>
          </div>

          {/* Legend & Stats */}
          <div className="flex-1 space-y-4">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-neutral-50 rounded-lg">
                <div className="text-xs text-neutral-500 uppercase tracking-wider">Investment</div>
                <div className="text-lg font-semibold text-neutral-900">
                  {formatCurrency(calculations.totalInvestment)}
                </div>
              </div>
              <div className="p-3 bg-neutral-50 rounded-lg">
                <div className="text-xs text-neutral-500 uppercase tracking-wider">Dilution</div>
                <div className={cn(
                  "text-lg font-semibold",
                  calculations.totalDilution > 0 ? "text-amber-600" : "text-neutral-900"
                )}>
                  {calculations.totalDilution.toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Ownership Breakdown */}
            <div className="space-y-2">
              <h4 className="text-xs uppercase tracking-wider text-neutral-500 font-medium">
                Ownership {viewMode !== 'current' && '(Post-Dilution)'}
              </h4>
              {pieSegments.map((segment) => {
                const colors = TYPE_COLORS[segment.type];
                return (
                  <motion.div
                    key={segment.id}
                    className={cn(
                      "flex items-center justify-between p-2 rounded-lg transition-colors",
                      hoveredSegment === segment.id ? "bg-neutral-100" : "bg-transparent"
                    )}
                    onMouseEnter={() => setHoveredSegment(segment.id)}
                    onMouseLeave={() => setHoveredSegment(null)}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: segment.color }}
                      />
                      <span className="text-sm text-neutral-700">{segment.name}</span>
                    </div>
                    <span className={cn("text-sm font-medium", colors?.text || "text-neutral-900")}>
                      {segment.percentage.toFixed(1)}%
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Individual Term Sheet Toggles */}
      {hasTermSheets && (
        <div className="border-t border-neutral-100 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs uppercase tracking-wider text-neutral-500 font-medium">
              Term Sheets
            </h4>
            {viewMode === 'custom' && selectedTermSheets.size > 0 && (
              <button
                onClick={() => {
                  setSelectedTermSheets(new Set());
                  setViewMode('current');
                }}
                className="text-xs text-neutral-500 hover:text-neutral-700"
              >
                Clear selection
              </button>
            )}
          </div>
          <div className="space-y-2">
            {termSheets.map((ts) => {
              const isCommitted = ts.status === 'accepted';
              const isPending = ['received', 'reviewing', 'negotiating'].includes(ts.status);
              const isActive = activeTermSheets.some(a => a.id === ts.id);
              const isSelected = selectedTermSheets.has(ts.id);
              const postMoney = ts.preMoneyValuation + ts.investmentAmount;
              const dilution = (ts.investmentAmount / postMoney) * 100;
              
              return (
                <motion.div
                  key={ts.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={cn(
                    "p-3 rounded-lg border cursor-pointer transition-all",
                    isActive
                      ? isCommitted
                        ? "bg-emerald-50 border-emerald-200"
                        : "bg-yellow-50 border-yellow-200"
                      : "bg-white border-neutral-100 hover:border-neutral-300"
                  )}
                  onClick={() => toggleTermSheet(ts.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected || (viewMode !== 'custom' && isActive)}
                        onChange={() => toggleTermSheet(ts.id)}
                        className="w-4 h-4 rounded border-neutral-300"
                      />
                      <div>
                        <div className="font-medium text-sm text-neutral-900">{ts.fundName}</div>
                        <div className="text-xs text-neutral-500">
                          {formatCurrency(ts.investmentAmount)} at {formatCurrency(ts.preMoneyValuation)} valuation
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={cn(
                        "text-sm font-semibold",
                        isCommitted ? "text-emerald-600" : isPending ? "text-yellow-600" : "text-neutral-400"
                      )}>
                        {dilution.toFixed(1)}% dilution
                      </div>
                      <div className="text-xs text-neutral-400 capitalize">
                        {ts.status.replace('_', ' ')}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State for Term Sheets */}
      {!hasTermSheets && hasCapTable && (
        <div className="text-center py-4 text-neutral-400 text-sm border-t border-neutral-100">
          No term sheets to simulate
        </div>
      )}
    </div>
  );
}

// ============================================
// RE-EXPORT FOR BACKWARDS COMPATIBILITY
// ============================================

export interface DilutionSegment {
  id: string;
  name: string;
  percentage: number;
  value: number;
  category: 'committed' | 'pending' | 'founders' | 'other';
  fundName?: string;
}

export interface DilutionChartProps extends DilutionSimulatorProps {
  segments?: DilutionSegment[];
  foundersPercentage?: number;
  showTable?: boolean;
}
